import { useState, type SyntheticEvent } from 'react';
import { BrandBadge } from './BrandBadge.js';
import { logoFit, providerArtBackground, type LogoFit } from './providerArtFit.js';

interface ProviderArtProps {
  name: string;
  logoUrl: string | null | undefined;
  brandColor?: string | null;
  /** Brand badge file code at the top-right (see features/games/brandBadge.ts); none when null. */
  badgeCode?: string | null;
  /** Size + rounding of the frame; the art inherits the rounding. */
  className?: string;
  /** Type size of the name drawn when the camp has no logo. */
  nameClassName?: string;
}

/**
 * The art card of a game camp, captioned with its name like a game card: its
 * logo with the name over a scrim at the foot, else the name alone, large, on
 * the camp's brand gradient. The frame's text is its accessible name, so a
 * link or button wrapping it needs no label of its own.
 */
export function ProviderArt({ name, logoUrl, brandColor, badgeCode = null, className = '', nameClassName = '' }: ProviderArtProps) {
  const [fit, setFit] = useState<LogoFit>('fill');
  const onLoad = (e: SyntheticEvent<HTMLImageElement>) =>
    setFit(logoFit(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight));

  return (
    <span
      data-provider-art=""
      className={`relative grid place-items-center overflow-hidden ${className}`}
      // A filling art tile brings its own background; a brand tint would only
      // show through its rounded corners as a coloured rim.
      style={{ background: logoUrl && fit === 'fill' ? 'var(--bg-raised)' : providerArtBackground(brandColor) }}
    >
      {logoUrl ? (
        <>
          <img
            src={logoUrl}
            alt=""
            loading="lazy"
            data-fit={fit}
            onLoad={onLoad}
            className={
              fit === 'fill'
                ? 'absolute inset-0 h-full w-full object-cover'
                : 'max-h-[45%] max-w-[78%] object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,.45)]'
            }
          />
          <span
            aria-hidden
            className="absolute inset-0 bg-gradient-to-t from-[rgba(5,7,14,.95)] via-transparent to-[rgba(5,7,14,.2)]"
          />
          <span className="absolute inset-x-0 bottom-0 px-2.5 pb-2.5 pt-6 text-left">
            <span className="text-[13px] font-extrabold leading-tight text-white line-clamp-2 drop-shadow-[0_2px_6px_rgba(0,0,0,.6)]">
              {name}
            </span>
          </span>
        </>
      ) : (
        <span
          className={`px-2 text-center font-extrabold leading-tight text-white break-words line-clamp-3 drop-shadow-[0_2px_8px_rgba(0,0,0,.55)] ${nameClassName}`}
        >
          {name}
        </span>
      )}
      <BrandBadge code={badgeCode} label={name} />
    </span>
  );
}
